/**
 * Tier labels for the billing UI. IDs must match backend `plan_config.PLAN_TIERS`
 * and Autumn product/plan IDs (`free`, `startup`, `growth`, `enterprise`).
 */
export type BillingPlanTierId = 'free' | 'startup' | 'growth' | 'enterprise';

const FEATURE_LABELS: Record<string, string> = {
  'ide-cli': 'Advanced IDE & CLI reviews',
  'security-scanning': 'Security scanning',
  'system-docs': 'System documentation',
  'team-insights': 'Team insights',
  'dora-metrics': 'DORA metrics',
  'slack-reporting': 'Slack reporting',
  sso: 'SSO & identity management',
  'org-visibility': 'Org-wide visibility',
  'audit-reports': 'Audit-ready reports',
};

function labels(ids: readonly string[]): string[] {
  return ids.map((id) => FEATURE_LABELS[id] ?? id);
}

export interface BillingPlanTierDisplay {
  id: BillingPlanTierId;
  name: string;
  /** Short positioning line under the title */
  subtitle: string;
  priceMonthlyUsd: number;
  /** Shown next to price — platform fee is flat per tier */
  priceDescriptor: string;
  headcountLabel: string;
  tokensSummary: string;
  tokensDetail: string;
  features: readonly string[];
  /** Middle tier — subtle emphasis in the grid */
  recommended?: boolean;
  /** Enterprise-style: hide price, show contact CTA */
  contactSales?: boolean;
}

export const BILLING_PLAN_TIERS: readonly BillingPlanTierDisplay[] = [
  {
    id: 'free',
    name: 'Free Trial',
    subtitle: 'Try Entelligence on your repos',
    priceMonthlyUsd: 0,
    priceDescriptor: 'No credit card',
    headcountLabel: 'Any team size',
    tokensSummary: '25M tokens',
    tokensDetail: 'Included per week',
    features: [
      'Core PR & code reviews',
      'IDE essentials',
      '25M tokens per week included',
    ],
  },
  {
    id: 'startup',
    name: 'Startup',
    subtitle: 'For lean engineering orgs',
    priceMonthlyUsd: 750,
    priceDescriptor: 'Platform / month',
    headcountLabel: '0–15 engineers',
    tokensSummary: '150M tokens',
    tokensDetail: 'Included per month',
    features: labels(['ide-cli', 'security-scanning', 'system-docs']),
  },
  {
    id: 'growth',
    name: 'Growth',
    subtitle: 'Scaling teams & visibility',
    priceMonthlyUsd: 2500,
    priceDescriptor: 'Platform / month',
    headcountLabel: '15–50 engineers',
    tokensSummary: '500M tokens',
    tokensDetail: 'Included per month',
    features: labels([
      'ide-cli',
      'security-scanning',
      'system-docs',
      'team-insights',
      'dora-metrics',
      'slack-reporting',
    ]),
    recommended: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    subtitle: 'Governance & highest limits',
    priceMonthlyUsd: 0,
    priceDescriptor: 'Custom pricing',
    headcountLabel: '51+ engineers',
    tokensSummary: '1B tokens',
    tokensDetail: 'Included per month',
    features: labels([
      'ide-cli',
      'security-scanning',
      'system-docs',
      'team-insights',
      'dora-metrics',
      'slack-reporting',
      'sso',
      'org-visibility',
      'audit-reports',
    ]),
    contactSales: true,
  },
] as const;
